import { Fragment, HTMLAttributes } from 'react'
import { map } from 'lodash'
import Link from 'next/link'

import Breadcrumbs from '@/components/base/breadcrumbs'
import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Image from '@/components/base/lazy-image'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useSchema } from '@/contexts/schema'
import { useAppStore } from '@/contexts/store'
import { useLoaderStore } from '@/store/loader'

import ButtonMask from '../base/button-mask'
import { cn } from '@/lib/utils'
import { WPImage, WPMedia } from '@/types'

export type ProductHeroProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    content: string
    notes: string
    link: string
    media: WPMedia
    certifications: { acf: { image: WPImage } }[]
    description: {
      title: string
      paragraph: string
    }
    specifications: {
      title: string
      table: {
        label: string
        value: string
      }[]
    }
    downloads: {
      technical_sheet: {
        title: string
        url: string
      }
      items: {
        item: {
          label: string
          file: {
            title: string
            url: string
          }
        }
      }[]
    }
  }>

const Certifications = ({
  list,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & { list: ProductHeroProps['certifications'] }) => {
  return (
    <div
      className={cn('grow basis-1/2 items-center space-x-[0.7rem] md:justify-end', className)}
      {...props}
    >
      {map(list, ({ acf }, index) => (
        <div key={`certification-${index}`} className="h-[2.5rem] w-auto">
          <Image
            src={acf?.image.url}
            alt={acf?.image.alt || ''}
            width={acf?.image.width}
            height={acf?.image.height}
            className="h-full w-auto"
          />
        </div>
      ))}
    </div>
  )
}

const ProductHero = ({
  title,
  content,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  notes,
  media,
  downloads,
  certifications,
  description,
  specifications,
  ...props
}: ProductHeroProps) => {
  const { schema } = useSchema()
  const { strings } = useAppStore()
  const setLoaderState = useLoaderStore((state) => state.setState)

  const handleLoad = () => {
    setLoaderState({ pageLoaded: true, loaded: true })
  }

  return (
    <Section {...props}>
      <Container>
        <Grid className="relative grid-rows-[auto_1fr] items-start gap-y-[4.2rem]">
          <div
            className={cn(
              'col-span-2 md:order-1 md:col-span-6 lg:col-span-12',
              'grid gap-y-[3rem]'
            )}
          >
            {schema ? <Breadcrumbs schema={schema} /> : null}
            {title ? (
              <Typography variant="main-title-default" component="h1">
                {title}
              </Typography>
            ) : null}
          </div>

          <div
            className={cn(
              'md:sticky md:top-[calc(8.4rem+22rem+2.4rem+3rem)]',
              'col-span-2 md:order-3 md:col-span-3 lg:col-span-6',
              'product-hero-min-h flex flex-col justify-between'
            )}
          >
            {media ? (
              <div className="flex w-full justify-center md:justify-end">
                <div
                  className={cn(
                    'relative aspect-square w-full',
                    'max-w-[28rem] md:max-w-[32rem]',
                    'md:-mt-[8.4rem]'
                  )}
                >
                  <Media onLoad={handleLoad} className="object-contain" {...media} />
                </div>
              </div>
            ) : null}

            <Certifications list={certifications} className="hidden md:flex" />
          </div>

          <div className={cn('col-span-2 md:order-2 md:col-span-3 lg:col-span-6')}>
            <div className="grid gap-y-[7.8rem]">
              <div className="grid gap-y-[3rem]">
                {content ? (
                  <Typography variant="standard" paragraph className="capitalize">
                    {content}
                  </Typography>
                ) : null}

                <Certifications list={certifications} className="md:hidden" />

                <div
                  className={cn(
                    'flex flex-col items-start space-y-[0.7rem]',
                    'lg:flex-row lg:space-x-[0.7rem] lg:space-y-0',
                    'w-full lg:w-auto'
                  )}
                >
                  {downloads?.technical_sheet ? (
                    <Button
                      variant="primary"
                      className="w-full lg:w-auto"
                      endIcon={<SVGIcon symbol="document" className="h-[2rem] w-[1.5rem]" />}
                      asChild
                    >
                      <Link
                        href={downloads.technical_sheet.url}
                        // download={downloads.technical_sheet.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ButtonMask>
                          <Typography variant="button">
                            {strings?.labels?.technical_sheet_cta}
                          </Typography>
                        </ButtonMask>
                      </Link>
                    </Button>
                  ) : null}

                  {map(downloads?.items, ({ item: { label, file } }, index) => (
                    <Fragment key={`download-button-${index}`}>
                      {file && file.url ? (
                        <Button
                          variant="secondary"
                          className="min-w-auto w-full lg:w-auto"
                          endIcon={<SVGIcon symbol="document" className="h-[2rem] w-[1.5rem]" />}
                          asChild
                        >
                          <Link
                            href={file.url}
                            download={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ButtonMask>
                              <Typography variant="button">{label}</Typography>
                            </ButtonMask>
                          </Link>
                        </Button>
                      ) : null}
                    </Fragment>
                  ))}
                </div>
              </div>

              {description ? (
                <div className={cn('grid gap-y-[4rem]', 'border-t border-black pt-[1.5rem]')}>
                  {description?.title ? (
                    <Typography variant="standard" component="h3">
                      {description?.title}
                    </Typography>
                  ) : null}

                  {description?.paragraph ? (
                    <Typography paragraph className="font-helvetica text-[1.6rem] leading-[150%]">
                      {description?.paragraph}
                    </Typography>
                  ) : null}
                </div>
              ) : null}

              {specifications ? (
                <div className={cn('grid gap-y-[4rem]', 'border-t border-black pt-[1.5rem]')}>
                  {specifications?.title ? (
                    <Typography variant="standard" component="h3">
                      {specifications?.title}
                    </Typography>
                  ) : null}

                  <div className={cn('grid', 'border-t border-black')}>
                    {specifications?.table
                      ? map(specifications?.table, ({ label, value }, index) => (
                          <div
                            key={`row-${index}`}
                            className={cn(
                              'grid grid-cols-[1fr_2fr]',
                              'border-b border-black py-[1.5rem]'
                            )}
                          >
                            <Typography
                              paragraph
                              className="font-helvetica text-[1.2rem] leading-[150%]"
                            >
                              {label}
                            </Typography>
                            <Typography
                              paragraph
                              className="font-helvetica text-[1.2rem] leading-[150%]"
                            >
                              {value}
                            </Typography>
                          </div>
                        ))
                      : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default ProductHero
